import { useRouter, useRoute } from 'vue-router'

export const useSmoothScroll = () => {
  const router = useRouter()
  const route = useRoute() 

  const getStickyHeaderOffset = () => {
    if (!import.meta.client) return 0

    const isStickyOrFixed = (el: Element | null) => {
      if (!el) return false
      const pos = window.getComputedStyle(el).position
      return pos === 'sticky' || pos === 'fixed'
    }

    const topBar = document.querySelector('.top-bar') as HTMLElement | null
    const navBar = document.querySelector('.navbar') as HTMLElement | null

    let offset = 0
    if (topBar && isStickyOrFixed(topBar)) offset += topBar.getBoundingClientRect().height
    if (navBar && isStickyOrFixed(navBar)) offset += navBar.getBoundingClientRect().height

    // Small breathing room so headings aren't flush to the sticky header.
    return Math.ceil(offset + 8)
  }

  const closeNavbarIfOpen = async () => {
    if (!import.meta.client) return

    const collapseEl = document.getElementById('navbarNav')
    if (!collapseEl || !collapseEl.classList.contains('show')) return

    const { $bootstrap } = useNuxtApp()
    const Collapse = ($bootstrap as any)?.Collapse
    if (!Collapse?.getOrCreateInstance) return

    await new Promise<void>((resolve) => {
      const onHidden = () => resolve()
      collapseEl.addEventListener('hidden.bs.collapse', onHidden, { once: true })

      // If Bootstrap events don't fire for any reason, don't hang.
      setTimeout(resolve, 450)

      const instance = Collapse.getOrCreateInstance(collapseEl, { toggle: false })
      instance?.hide?.()
    })

    // Let layout settle after the collapse transition.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  }

  const scrollToElement = async (selector: string, updateHash = false) => {
    if (!import.meta.client) return

    const sanitizedSelector = useSanitize(selector, 'text')
    const id = sanitizedSelector.replace(/^#/, '')
    const element = document.getElementById(id)

    if (!element) return

    // If the user clicked from the mobile header, the Bootstrap nav may be expanded
    // and covering the content. Close it first so the offset is computed correctly.
    await closeNavbarIfOpen()
    const offset = getStickyHeaderOffset()

    const top = element.getBoundingClientRect().top + window.pageYOffset - offset
    window.scrollTo({ top, behavior: 'smooth' })

    if (updateHash) {
      const originalId = element.id
      element.id = ''

      router.replace({ path: route.path, query: route.query, hash: `#${id}` }).finally(() => {
        requestAnimationFrame(() => {
          element.id = originalId
        })
      })
    }
  }

  return { scrollToElement }
}